import React, { useState, useEffect } from 'react';
import RecipeCard from './RecipeCard';
import { useAuth } from '../context/AuthContext';
import { useCSRF } from '../utils/csrfUtils';
import supabase from '../supabaseClient';
import Masonry from 'react-masonry-css';
import { useGetAllDislikedRecipes } from '../hooks/dislikeRecipe';
import { Spinner } from 'react-bootstrap';

function TrendingRecipes({ sessionToken, searchTerm = '', onMealAdded }) {
    const [trendingRecipes, setTrendingRecipes] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [favoriteRecipes, setFavoriteRecipes] = useState([]);
    const [dislikedRecipes, setDislikedRecipes] = useState([]);
    const [error, setError] = useState(null);
    const { pgUser } = useAuth();
    const { fetchWithCSRF } = useCSRF();
    const { sessionToken: authSessionToken, setSessionToken } = useAuth();
    const { getAllDislikedRecipes } = useGetAllDislikedRecipes();

    // Wait for both session token and pgUser to be ready
    useEffect(() => {
        if (!sessionToken || !pgUser) {
            setIsLoading(true);
            return;
        }
        setIsLoading(false);
    }, [sessionToken, pgUser]);

    // If session token not passed from parent, get it from auth context
    useEffect(() => {
        if (!authSessionToken) {
            const getSessionToken = async () => {
                const { data: { session } } = await supabase.auth.getSession();
                if (session?.access_token) {
                    setSessionToken(session.access_token);
                }
            };
            getSessionToken();
        }
    }, [authSessionToken, setSessionToken]);

    const fetchFavoritesAndDislikes = async () => {
        if (!sessionToken || !pgUser) return;

        try {
            // Fetch favorites
            const favoritesResponse = await fetch('/api/v1/recipe-favorite/my-favorites', {
                headers: {
                    'Authorization': `Bearer ${sessionToken}`
                }
            });

            if (!favoritesResponse.ok) {
                throw new Error(`Failed to fetch favorites: ${favoritesResponse.status}`);
            }

            const favoritesData = await favoritesResponse.json();
            if (Array.isArray(favoritesData)) {
                setFavoriteRecipes(favoritesData);
            } else {
                console.error('Invalid data format for favorites');
                setFavoriteRecipes([]);
            }

            // Fetch dislikes
            const dislikesData = await getAllDislikedRecipes(sessionToken);
            if (Array.isArray(dislikesData)) {
                setDislikedRecipes(dislikesData);
            }
        } catch (err) {
            console.error('Error fetching favorites/dislikes:', err);
        }
    };

    useEffect(() => {
        const fetchTrendingRecipes = async () => {
            if (!sessionToken || !pgUser) return;

            try {
                const response = await fetchWithCSRF('/api/v1/recipe/trending', {
                    headers: {
                        'Authorization': `Bearer ${sessionToken}`
                    }
                });

                if (!response.ok) {
                    throw new Error(`Failed to fetch trending recipes: ${response.status}`);
                }

                const data = await response.json();
                if (Array.isArray(data)) {
                    setTrendingRecipes(data);
                } else {
                    console.error('Invalid data format for trending recipes');
                    setTrendingRecipes([]);
                }
            } catch (err) {
                console.error('Error fetching trending recipes:', err);
                setError(err.message || 'Failed to load trending recipes');
            } finally {
                setIsLoading(false);
            }
        };

        fetchTrendingRecipes();
        fetchFavoritesAndDislikes();
    }, [pgUser, sessionToken, fetchWithCSRF]);

    if (isLoading) {
        return (
            <div className="text-center my-4">
                <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading trending recipes...</span>
                </Spinner>
            </div>
        );
    }

    if (error) {
        return (
            <div className="alert alert-danger mt-4">
                <p className="mb-1">Error loading trending recipes. Please refresh the page or try again later.</p>
                <p className="mb-1">{error}</p>
            </div>
        );
    }

    if (trendingRecipes.length === 0) {
        return (
            <div className="text-center my-5">
                <p className="text-muted">No trending recipes available at the moment.</p>
            </div>
        );
    }

    const breakpointColumns = {
        default: 4,
        1100: 3,
        700: 2,
        500: 1
    };

    // If there's only one recipe, force single column layout
    if (trendingRecipes.length === 1) {
        breakpointColumns.default = 1;
    }

    // Filter recipes based on search term
    const filteredRecipes = trendingRecipes.filter(recipe => {
        const searchLower = searchTerm.toLowerCase();
        
        // Search by recipe name
        if (recipe.name.toLowerCase().includes(searchLower)) {
            return true;
        }
        
        // Search by ingredient names (if ingredients are available)
        if (recipe.ingredients && Array.isArray(recipe.ingredients)) {
            return recipe.ingredients.some(ingredient => 
                ingredient.ingredient_name && 
                ingredient.ingredient_name.toLowerCase().includes(searchLower)
            );
        }
        
        return false;
    });

    if (filteredRecipes.length === 0 && searchTerm) {
        return <div className="text-center my-5">No trending recipes found matching "{searchTerm}"</div>;
    }

    return (
        <div className="container">
            <Masonry
                breakpointCols={breakpointColumns}
                className="my-masonry-grid"
                columnClassName="my-masonry-grid_column"
            >
                {filteredRecipes.map(recipe => (
                    <div 
                        key={recipe.id} 
                        style={{ 
                            position: 'relative',
                            minWidth: '250px',
                            width: '100%'
                        }}
                    >
                        <RecipeCard 
                            recipe={recipe}
                            isFavorite={favoriteRecipes.some(favorite => favorite.recipe_id === recipe.id)}
                            isDisliked={Array.isArray(dislikedRecipes) && dislikedRecipes.includes(Number(recipe.id))}
                            onMealAdded={onMealAdded}
                        />
                    </div>
                ))}
            </Masonry>
        </div>
    );
}

export default TrendingRecipes;

